import React, { useEffect, useState } from 'react';

import IconInformationCircle from '@/assets/icons/heroIcons/InformationCircle.svg';
import IconCheck from '@/assets/icons/heroIcons/Circle-check.svg';
import IconCross from '@/assets/icons/heroIcons/Circle-cross.svg';
import { Tooltip } from '@/components/Tooltip';
import { TabsButtons } from '@/components/buttons';
import { mergeClasses } from '@/utils/style';
import { Link } from '@/components/links';
import { ComparisonTableCategory } from '@/types/comparisonTable';

interface Props {
	titles: {
		left: string;
		right: string;
	};
	categories: ComparisonTableCategory[];
	isSecondColumnActive?: boolean;
	headerClassName?: string;
	coloredHeaderBgClassName?: string;
	primaryColumn?: number;
	footer?: {
		title: string | React.JSX.Element;
		left: string | boolean;
		right: string | boolean;
	};
	badge?: string;
}

function Answer({
	content,
	className,
}: {
	content: string | boolean;
	className?: string;
}): React.ReactNode {
	return (
		<div
			className={mergeClasses(
				'flex shrink-0 items-center justify-end pr-6 md:w-52 md:justify-center md:pr-0',
				className
			)}
		>
			{typeof content === 'string' ? (
				<b className="font-semibold">{content}</b>
			) : content ? (
				<IconCheck />
			) : (
				<IconCross />
			)}
		</div>
	);
}

function ComparisonTable({
	titles,
	categories,
	isSecondColumnActive,
	headerClassName,
	coloredHeaderBgClassName,
	primaryColumn = 1,
	footer,
	badge,
}: Props): React.ReactNode {
	const [isFirstColumnActive, setIsFirstColumnActive] = useState(true);

	useEffect(() => {
		if (isSecondColumnActive) {
			setIsFirstColumnActive(false);
		}
	}, [isSecondColumnActive]);

	return (
		<div className="relative flex flex-col items-center gap-6">
			{badge && (
				<div className="mt-4 h-24 w-25 -rotate-[15deg] rounded-full bg-red-500 p-2 text-center text-base font-semibold text-white md:hidden">
					{badge}
				</div>
			)}
			<div className="w-full md:hidden">
				<TabsButtons
					id="comparisonTabs"
					activeTab={isFirstColumnActive ? 'firstTab' : 'secondTab'}
					tabs={[
						{
							id: 'firstTab',
							label: titles.left,
							onClick: () => setIsFirstColumnActive(true),
						},
						{
							id: 'secondTab',
							label: titles.right,
							onClick: () => setIsFirstColumnActive(false),
						},
					]}
				/>
			</div>

			<div className="w-full md:py-4">
				<div className="relative mx-auto max-w-[60rem] rounded-xl bg-white pt-6 pb-7 md:rounded-3xl md:bg-blue-50">
					{badge && (
						<div className="absolute -top-3 left-5 z-10 hidden h-24 w-25 -rotate-[15deg] rounded-full bg-red-500 p-2 text-center text-base font-semibold text-white md:block">
							{badge}
						</div>
					)}
					<div
						className={mergeClasses(
							'absolute -top-4 -bottom-4 z-0 hidden w-52 overflow-hidden rounded-3xl bg-white md:block',
							{ 'right-52': primaryColumn === 1 },
							{ 'right-0': primaryColumn === 2 }
						)}
					>
						<div className={mergeClasses('h-26 w-full bg-blue-500', coloredHeaderBgClassName)} />
					</div>
					<div
						className={mergeClasses(
							'relative hidden flex-row items-center text-xl md:flex md:pb-7',
							{ 'md:pb-[5.25rem]': badge !== undefined }
						)}
					>
						<div className="w-full flex-shrink-1 px-8 py-2" />
						<Answer
							content={titles.left}
							className={mergeClasses(
								'px-4! text-center',
								{ 'text-white': primaryColumn === 1 },
								headerClassName
							)}
						/>
						<Answer
							content={titles.right}
							className={mergeClasses(
								'px-4! text-center',
								{ 'text-white': primaryColumn === 2 },
								headerClassName
							)}
						/>
					</div>
					<div className="flex flex-col gap-6 md:gap-8">
						{categories.map((category, categoryIndex) => (
							<div key={`category-${categoryIndex}`} className="relative">
								<div className="flex flex-col gap-3 md:gap-4">
									<p
										className={mergeClasses(
											'px-6 text-lg font-semibold md:px-8 md:text-xl',
											category.to ? 'text-blue-500' : ''
										)}
									>
										{category.to ? (
											<Link
												to={category.to}
												analyticsParam="comparisonTableLink"
												testId="comparisonTableLink"
											>
												{category.title}
											</Link>
										) : (
											<span>{category.title}</span>
										)}
										{category.tooltip && (
											<span className="relative top-1 ml-2 inline-block text-base font-normal md:text-base">
												<Tooltip tooltip={category.tooltip} trigger={['hover', 'click', 'focus']}>
													<span className="text-light-gray-500 inline-block">
														<IconInformationCircle />
													</span>
												</Tooltip>
											</span>
										)}
									</p>

									{category.items.map((item, itemIndex) => (
										<div
											className="relative flex flex-row items-center"
											key={`category-${categoryIndex}-item-${itemIndex}`}
										>
											<div className="w-full flex-shrink-1 px-6 md:px-8">
												{item.to ? (
													<Link
														to={item.to}
														analyticsParam="comparisonTableItemLink"
														testId="comparisonTableItemLink"
														className="text-sn text-blue-600 underline md:text-base"
													>
														{item.title}
													</Link>
												) : (
													<span className="text-sn md:text-base">{item.title}</span>
												)}
												{item.tooltip && (
													<div className="relative top-1 ml-2 inline-block">
														<Tooltip tooltip={item.tooltip}>
															<span className="text-light-gray-500 inline-block">
																<IconInformationCircle />
															</span>
														</Tooltip>
													</div>
												)}
											</div>
											<Answer
												content={item.left}
												className={mergeClasses(!isFirstColumnActive && 'hidden md:flex')}
											/>
											<Answer
												content={item.right}
												className={mergeClasses(isFirstColumnActive && 'hidden md:flex')}
											/>
										</div>
									))}
								</div>
							</div>
						))}
						{footer && (
							<div className="flex flex-row gap-2 md:gap-4">
								<div className="w-full flex-shrink-1 px-6 md:px-8">
									<p className="text-lg font-semibold md:text-xl">{footer.title}</p>
								</div>
								<div className="relative flex flex-row items-center justify-center">
									<Answer
										content={footer.left}
										className={mergeClasses(!isFirstColumnActive && 'hidden md:flex')}
									/>
									<Answer
										content={footer.right}
										className={mergeClasses(isFirstColumnActive && 'hidden md:flex')}
									/>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

export default ComparisonTable;
